import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'jobsurf_cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    if (stored === 'essential_only') disableAnalytics();
    if (stored === 'all_accepted') {
      // SDK might load slightly after this effect runs, so retry briefly.
      const grant = () => { try { if (window.ttq && typeof window.ttq.grantConsent === 'function') window.ttq.grantConsent(); } catch (e) {} };
      grant();
      setTimeout(grant, 500);
      setTimeout(grant, 1500);
    }
  }, []);

  const disableAnalytics = () => {
    window['ga-disable-GA_MEASUREMENT_ID'] = true;
    // TikTok Pixel — revoke consent if the SDK is present.
    try { if (window.ttq && typeof window.ttq.revokeConsent === 'function') window.ttq.revokeConsent(); } catch (e) {}
  };

  const enableAnalytics = () => {
    // TikTok Pixel — grant consent so the event queue flushes.
    try { if (window.ttq && typeof window.ttq.grantConsent === 'function') window.ttq.grantConsent(); } catch (e) {}
  };

  const acceptAll = () => {
    localStorage.setItem(STORAGE_KEY, 'all_accepted');
    enableAnalytics();
    setVisible(false);
  };

  const essentialOnly = () => {
    localStorage.setItem(STORAGE_KEY, 'essential_only');
    disableAnalytics();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 320 }}
          className="fixed bottom-3 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-4 sm:w-auto sm:max-w-2xl z-[9999]"
          data-testid="cookie-consent-banner"
        >
          <div className="bg-[#1c1c1c]/95 backdrop-blur-md border border-white/[0.08] rounded-full shadow-xl px-3 py-2 sm:px-4 sm:py-2 flex items-center gap-2 sm:gap-3">
            <Cookie className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <p className="text-zinc-300 text-[11px] sm:text-xs leading-tight flex-1 min-w-0">
              We use cookies.{' '}
              <Link to="/privacy" className="text-indigo-400 hover:underline whitespace-nowrap" onClick={() => setVisible(false)}>
                Learn more
              </Link>
            </p>
            <button
              onClick={essentialOnly}
              className="px-2.5 py-1 text-[11px] sm:text-xs font-medium text-zinc-400 hover:text-white transition-colors whitespace-nowrap"
              data-testid="cookie-decline-btn"
            >
              Essential
            </button>
            <button
              onClick={acceptAll}
              className="px-3 py-1 bg-teal-500 text-zinc-950 text-[11px] sm:text-xs font-semibold rounded-full hover:bg-teal-400 transition-colors whitespace-nowrap"
              data-testid="cookie-accept-btn"
            >
              Accept
            </button>
            <button
              onClick={essentialOnly}
              className="text-zinc-600 hover:text-zinc-300 transition-colors shrink-0 ml-0.5"
              data-testid="cookie-close-btn"
              aria-label="Dismiss cookie banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
